import React, { useState, useEffect } from "react";
import Select from "react-select";

import { exportUsersCustomized } from "../../api/userreport";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../data/redux/store";
import { fetchUsersByClientId } from "../../api/todo";
interface OptionType {
  label: string;
  value: string;
  isDisabled?: boolean;
}
const UserReport = () => {
  const [fields, setFields] = useState<OptionType[]>([]);
  const [exportType, setExportType] = useState<OptionType | null>(null);
  const [year, setYear] = useState("");
  const [month, setMonth] = useState<OptionType | null>(null);
  const [userOptions, setUserOptions] = useState<OptionType[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<OptionType[]>([]);

  const clientId = useSelector((state: RootState) => state.auth.client_id);

  const fieldOptions: OptionType[] = [
    { value: "ALL", label: "All Fields" },
    { value: "user_name", label: "User Name" },
    { value: "total_leads", label: "Total Leads" },
    { value: "active_leads", label: "Active Leads" },
    { value: "contacted_leads", label: "Contacted Leads" },
    { value: "not_contacted_leads", label: "Not Contacted Leads" },
    { value: "closed_leads", label: "Closed Leads" },
    { value: "open_leads", label: "Open Leads" },
    { value: "won_leads", label: "Won Leads" },
    { value: "won_deals", label: "Won Deals" },
    { value: "open_deals", label: "Open Deals" },
    { value: "lost_deals", label: "Lost Deals" },
    { value: "closed_deals", label: "Closed Deals" },
    { value: "lead_status", label: "Lead Status" },
    { value: "deal_status", label: "Deal Status" },
    { value: "lead_source", label: "Lead Source" },
    { value: "lead_type", label: "Lead Type" },
    { value: "product_type", label: "Product Type" },
    { value: "conversion_rate", label: "Conversion Rate" },
    { value: "contacts_this_month", label: "Contacts This Month" },
    { value: "leads_created_this_month", label: "Leads Created This Month" },
    { value: "deals_created_this_month", label: "Deals Created This Month" },
    { value: "deals_won_this_month", label: "Deals Won This Month" },
    { value: "total_revenue_this_month", label: "Revenue This Month" },
    { value: "incentive_percentage", label: "Incentive (%)" },
    { value: "calculated_incentive", label: "Calculated Incentive" },
    { value: "target", label: "Target" },
    { value: "achieved", label: "Achieved" },
    { value: "remaining_to_achieve", label: "Remaining To Achieve" },
  ];
  const exportTypeOptions: OptionType[] = [
    { value: "", label: "Choose Export Type", isDisabled: true },
    { value: "pdf", label: "Download as PDF", isDisabled: fields.length > 10 },
    { value: "excel", label: "Download as Excel" },
  ];
  const monthOptions: OptionType[] = Array.from({ length: 12 }, (_, i) => ({
    value: `${i + 1}`,
    label: new Date(0, i).toLocaleString("default", { month: "long" }),
  }));

  useEffect(() => {
    const loadUsers = async () => {
      try {
        if (!clientId) return;
        const users = await fetchUsersByClientId(clientId);
        const options = users.map((u) => ({
          label: u.name,
          value: u.id,
        }));
        setUserOptions(options);
      } catch (err) {
        console.error("Failed to load users");
      }
    };
    loadUsers();
  }, [clientId]);

  const resetForm = () => {
    setFields([]);
    setExportType(null);
    setYear("");
    setMonth(null);
    setSelectedUsers([]);
  };

  const handleDownload = () => {
    if (!fields.length || !exportType || !year || !selectedUsers.length) {
      toast.warning("Please fill all required fields.");
      return;
    }
    const isPdfTooWide =
      exportType && exportType.value === "pdf" && fields.length > 10;

    if (isPdfTooWide) {
      toast.warning(
        "PDF export supports up to 10 fields. Please reduce the selection or use Excel."
      );
      return;
    }
    exportUsersCustomized({
      fields: fields.find((f) => f.value === "ALL")
        ? fieldOptions
            .filter((f) => f.value !== "ALL")
            .map((f) => f.value)
            .join(",")
        : fields.map((f) => f.value).join(","),
      file_type: exportType.value as "excel" | "pdf",
      year: +year,
      month: month?.value ? +month.value : undefined,
      user_ids: selectedUsers.map((u) => u.value).join(","),
    });
    resetForm();
  };

  return (
    <div
      className="modal custom-modal fade effect-modal-show"
      id="user_report_modal"
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-bottom">
            <div>
              <h4 className="modal-title text-primary fw-semibold d-flex align-items-center gap-2">
                <i className="ti ti-file-download text-primary" />
                Export User Report
              </h4>
              <small className="text-muted">
                Customize fields, filters, and export format
              </small>
            </div>

            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <div className="modal-body">
            <form>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Fields<span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix="react-select"
                  className="select"
                  options={fieldOptions.filter(
                    (opt) => !fields.find((f) => f.value === opt.value)
                  )}
                  placeholder={
                    fields.length === 0
                      ? "Choose fields to include in report"
                      : `${fields.length} fields selected`
                  }
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  value={fields}
                  onChange={(selectedOptions) => {
                    const selected = selectedOptions as OptionType[];
                    if (selected.some((opt) => opt.value === "ALL")) {
                      setFields(fieldOptions.filter((f) => f.value !== "ALL")); // Select all except "ALL"
                    } else {
                      setFields(selected);
                    }
                  }}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: "auto",
                      height: "auto",
                      boxShadow: state.isFocused
                        ? "0 0 0 1px #ccc"
                        : base.boxShadow,
                      borderColor: state.isFocused ? "#ccc" : base.borderColor,
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      flexWrap: "wrap",
                      maxHeight: "100%",
                      overflowY: "auto",
                      paddingTop: "6px",
                      paddingBottom: "6px",
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      flexShrink: 0,
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "#eef2ff",
                      borderRadius: "8px",
                      padding: "2px 8px",
                      fontSize: "0.85rem",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "#1e40af",
                      fontWeight: 600,
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "#888",
                      ":hover": {
                        backgroundColor: "#ccc",
                        color: "#000",
                      },
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
                {fields.length > 0 && (
                  <div className="mt-2">
                    <span
                      className={`badge ${
                        fields.length > 10 ? "bg-danger" : "bg-secondary"
                      }`}
                    >
                      {fields.length} field{fields.length > 1 ? "s" : ""}{" "}
                      selected
                      {fields.length > 10 && " — PDF allows max 10"}
                    </span>
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Export Type <span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix="react-select"
                  className="select"
                  options={exportTypeOptions}
                  value={exportType}
                  onChange={(e) => setExportType(e)}
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Year<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  className="form-control"
                  placeholder="YYYY"
                  value={year}
                  onChange={(e) => {
                    if (/^\d{0,4}$/.test(e.target.value)) {
                      setYear(e.target.value);
                    }
                  }}
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Select Month</label>
                <Select
                  classNamePrefix="react-select"
                  className="select"
                  options={monthOptions}
                  value={month}
                  onChange={(e) => setMonth(e)}
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Select Users<span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix="react-select"
                  className="select"
                  options={userOptions.filter(
                    (opt) => !selectedUsers.find((f) => f.value === opt.value)
                  )}
                  isMulti
                  value={selectedUsers}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  onChange={(e) => setSelectedUsers(e as OptionType[])}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: "auto",
                      height: "auto",
                      boxShadow: state.isFocused
                        ? "0 0 0 1px #ccc"
                        : base.boxShadow,
                      borderColor: state.isFocused ? "#ccc" : base.borderColor,
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      flexWrap: "wrap",
                      maxHeight: "100%",
                      overflowY: "auto",
                      paddingTop: "6px",
                      paddingBottom: "6px",
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      flexShrink: 0,
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "#eef2ff",
                      borderRadius: "6px",
                      padding: "2px 8px",
                      fontSize: "0.85rem",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "#1e40af",
                      fontWeight: 600,
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "#888",
                      ":hover": {
                        backgroundColor: "#ccc",
                        color: "#000",
                      },
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
                {selectedUsers.length > 0 && (
                  <div className="mt-2">
                    <span className="badge bg-secondary">
                      {selectedUsers.length} user selected
                    </span>
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-between align-items-center mt-4">
                <small className="text-muted">
                  {!fields.length || !selectedUsers.length
                    ? "Select fields and users to enable export"
                    : "Ready to export user report"}
                </small>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-light"
                    data-bs-dismiss="modal"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleDownload}
                    data-bs-dismiss="modal"
                    disabled={!fields.length || !selectedUsers.length}
                  >
                    <i className="ti ti-download me-1" />
                    Export
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserReport;
